<template>
	<div class="coder-prompter">
		<DxLoadPanel :visible="isWorking" shading-color="rgba(0, 0, 0, 0.2)" container=".coder-prompter">
			<DxPosition of=".coder-prompter" my="center center" at="center center" />
		</DxLoadPanel>
		<DxTextBox :element-attr="{ class: 'ai-instructions' }" styling-mode="outlined" label="Assistant Instructions" :value="prompt" @update:value="prompt = $event" @enterKey="generateHtml">
			<DxTextBoxButton name="ai" location="before" :options="{ icon: 'moon', stylingMode: 'text', hint: 'AI', elementAttr: { class: 'animated-moon-button' } }" />
		</DxTextBox>
		<div v-if="currentError" class="dx-error-message reduce-padding">
			<i class="dx-icon-warning"></i><div class="dx-error-message-text">{{ currentError }}</div>
			<DxButton icon="close" hint="Dismiss" styling-mode="text" @click="currentError = null" />
		</div>
		<div class="coder-toolbar">
			<div v-if="showRefreshWarning" class="dx-error-message reduce-padding">
				<i class="dx-icon-warning"></i>
				<div class="refresh-warning-text">New placeholder images may take a few seconds to generate. Please use the <i class="dx-icon dx-icon-refresh" style="font-size: 16px;"></i> button to refresh these images.</div>
			</div>
		</div>
		<div class="coder-toolbar">
			<DxButton text="Generate" type="default" styling-mode="contained" @click="generateHtml" />
			<div class="spacer"></div>
			<div class="right-actions">
				<div class="generation-counters">{{ currentGenerationIndex + 1 }} / {{ generationHistory.length }}</div>
				<DxButton icon="refresh" :hint="'Refresh Images'" styling-mode="text" @click="refreshAllImages" />
				<DxButton icon="undo" :hint="'Undo'" styling-mode="text" @click="undoGeneration" />
				<DxButton icon="redo" :hint="'Redo'" styling-mode="text" @click="redoGeneration" />
			</div>
		</div>
		<DxTextArea label="HTML Code" height="250px" :value="currentCode" @update:value="textareaValueChanged" />
	</div>
</template>

<script setup>
/* eslint-disable */

// Coder prompter is a component that incorporates a textbox prompt instruction, textarea current code, and a few buttons for undo/redo/regenerate.
// It is used to generate 'code' in arbitrary situations - currently just HTML.

import { ref, defineProps, defineEmits, inject, watch, nextTick} from 'vue';
import DxTextBox, { DxButton as DxTextBoxButton } from 'devextreme-vue/text-box';
import { DxTextArea } from 'devextreme-vue/text-area';
import { DxButton } from 'devextreme-vue/button';
import { DxLoadPanel, DxPosition } from 'devextreme-vue/load-panel';

console.log("LOAD FOR CoderPrompter");
const props = defineProps([
	'type',			// Type of code to generate, one of: 'html'
	'initial',		// Initial code to start with
]);
const emit = defineEmits(['codeChanged']);
const $http = inject('$http');

// If needed, use this randomId function.
// const randomId = ref(crypto.randomUUID().replaceAll('-', ''));

const prompt = ref('');
const currentCode = ref('');
const generationHistory = ref([]);
const currentGenerationIndex = ref(0);
const isWorking = ref(false);
const currentError = ref(null);
const showRefreshWarning = ref(false);

// Set internal values when props change.
const ignoreNextPropChange = ref(false);
watch(props, () => {
	console.log("Props updated with ignore value = " + ignoreNextPropChange.value, props);
	if (ignoreNextPropChange.value) {
		ignoreNextPropChange.value = false;
		return;
	}
	currentCode.value = props.initial ? props.initial : '';
	generationHistory.value = [ currentCode.value ];
	currentGenerationIndex.value = 0;
}, { immediate: true });

// Undo/redo/add code texts to history.
const undoGeneration = () => {
	if (currentGenerationIndex.value > 0) {
		currentGenerationIndex.value--;
		currentCode.value = generationHistory.value[currentGenerationIndex.value];
		codeWasUpdated();
	}
};
const redoGeneration = () => {
	if (currentGenerationIndex.value < generationHistory.value.length - 1) {
		currentGenerationIndex.value++;
		currentCode.value = generationHistory.value[currentGenerationIndex.value];
		codeWasUpdated();
	}
};
const addNewGeneration = (newCode) => {
	// Because we could be in the middle of the history, e.g. 5/10, we need to truncate the history to the current index.
	generationHistory.value = generationHistory.value.slice(0, currentGenerationIndex.value + 1);
	generationHistory.value.push(newCode);
	currentCode.value = newCode;
	currentGenerationIndex.value = generationHistory.value.length - 1;
	codeWasUpdated();
};

const textareaValueChanged = (value) => {
	addNewGeneration(value);
};
const codeWasUpdated = () => {
	console.log("Signaling code update...");
	ignoreNextPropChange.value = true;
	emit('codeChanged', currentCode.value);
}

// We may need a re-render of the HTML code later on down the line, if there are any placeholder images.
// Our placeholder service can take several seconds to generate an image, and there is no way to know when it is ready.
// In the meantime, the service displays a generic "Loading" image.
const refreshAllImages = () => {
	// In order to trigger a re-render, we need to actually change the value being computed by Vue.
	const val = currentCode.value;
	currentCode.value = val + ' ';
	codeWasUpdated();
	nextTick(() => {
		currentCode.value = val;
		codeWasUpdated();
	});
};

// Generator for HTML code.
const generateHtml = () => {
	if (isWorking.value) {
		return;
	}
	isWorking.value = true;
	currentError.value = null;
	
	$http.post('/chat/coder', {
		type: 'html',
		message: prompt.value,
		context: currentCode.value,
	}).then(response => {
		if (response && response.data && response.data.error) {
			console.error("Error generating HTML", response.data);
			currentError.value = response.data.error;
			return;
		}
		if (response && response.data && response.data.code) {
			addNewGeneration(response.data.code);
			// Show the refresh warning if there are any new placeholder images.
			if (response.data.code.includes('placeholdr.dev')) {
				showRefreshWarning.value = true;
			}
		}
	}).catch(error => {
		console.error("Error generating HTML", error);
	}).finally(() => {
		isWorking.value = false;
	});
};

</script>

<style lang="scss">
.coder-prompter {
	.coder-toolbar {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-top: 8px;
		margin-bottom: 16px;
	}

	.coder-toolbar .spacer {
		flex: 1;
	}

	.coder-toolbar .right-actions {
		display: flex;
		align-items: center;
		gap: 4px;
	}

	.generation-counters {
		margin-top: 3px;
		margin-right: 10px;
		font-style: italic;
		color: #555;
	}

	.dx-error-message {
		margin-top: 4px;
	}

	// AI instructions wrapper with animated accent matching header toolbar moon
	.ai-instructions {
		position: relative;
		&.dx-textbox {
			background-color:#60bdb42e;
			&.dx-state-focused {
				background-color: transparent;
			}
		}

		&::before {
			content: '';
			position: absolute;
			left: 0;
			top: 0;
			bottom: 0;
			width: 4px;
			background-color: #009688;
			animation: ai-accent-color 3s infinite ease-in-out;
		}
	}
}

@keyframes ai-accent-color {
	0% { background-color: #009688; }
	25% { background-color: #7b1fa2; }
	50% { background-color: #7b1fa2; }
	75% { background-color: #009688; }
	100% { background-color: #009688; }
}
</style>
